'use client';

import { AlertTriangle, AtSign, ExternalLink, FileX, Loader2, Pause, Play, Share2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type MouseEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { toast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { cn } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { normalizePath, reportClawCreationOperation } from '../../../_utils';
import { getDownloadUrl } from '../../../apis';
import type { ClawContent, ClawContentType } from '../../../types';
import { useShareClawAsset } from '../../Chat/MediaBrowser/hooks/use-share-claw-asset';
import { ClawMarkdown } from '../../ClawMarkdown';
import { getPreviewTypeFromPath, isMarkdownFile, isTextPreviewFile } from '../../File/utils';
import { FileItem } from './FileItem';

interface MessageAttachmentListProps {
  files: ClawContent[];
  clawId?: string;
  align?: 'left' | 'right';
  shareSessionId?: string;
  shareMessageId?: string;
  onPreviewFile?: (filePath: string) => void;
  onQuoteFile?: (filePath: string) => void;
}

interface InlineAttachmentPreviewProps {
  filePath: string;
  fileType?: ClawContentType;
  status?: string;
  clawId?: string;
  onPreview?: (filePath: string) => void;
  onQuote?: (filePath: string) => void;
  onShare?: (filePath: string, fileType: 'image' | 'video') => void;
  sharingFilePath?: string;
}

const TEXT_PREVIEW_LINE_COUNT = 10;

interface AttachmentPreviewCacheValue {
  previewUrl?: string;
  textPreview?: {
    content: string;
    hasMore: boolean;
  };
}

const attachmentPreviewCache = new Map<string, AttachmentPreviewCacheValue>();

const getFallbackFileName = (filePath: string): string => {
  const segments = normalizePath(filePath).split('/').filter(Boolean);
  return segments[segments.length - 1] ?? filePath;
};

const trimTextPreview = (content: string) => {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  return {
    content: lines.slice(0, TEXT_PREVIEW_LINE_COUNT).join('\n'),
    hasMore: lines.length > TEXT_PREVIEW_LINE_COUNT,
  };
};

const QuoteButton = ({
  filePath,
  className,
  onQuote,
}: {
  filePath: string;
  className?: string;
  onQuote?: (filePath: string) => void | Promise<void>;
}) => {
  const t = useTranslations();
  const [busy, setBusy] = useState(false);
  if (!onQuote) return null;

  const handleQuote = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await onQuote(filePath);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      aria-label={t('claw.file.quote')}
      aria-busy={busy}
      disabled={busy}
      className={cn(
        'absolute inline-flex size-7 items-center justify-center rounded-full bg-black/45 light:bg-white/80 light:text-[#111111] text-system-white backdrop-blur-sm transition-colors hover:bg-black/65 light:hover:bg-white disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
      onClick={handleQuote}
    >
      {busy ? <Loader2 className="size-3.5 animate-spin" /> : <AtSign className="size-3.5" />}
    </button>
  );
};

const ShareButton = ({
  filePath,
  fileType,
  className,
  sharing,
  onShare,
}: {
  filePath: string;
  fileType: 'image' | 'video';
  className?: string;
  sharing?: boolean;
  onShare?: (filePath: string, fileType: 'image' | 'video') => void;
}) => {
  const t = useTranslations();
  if (!onShare) return null;

  return (
    <button
      type="button"
      aria-label={t('claw.assets.actions.share')}
      disabled={sharing}
      className={cn(
        'absolute inline-flex size-7 items-center justify-center rounded-full bg-black/45 light:bg-white/80 light:text-[#111111] text-system-white backdrop-blur-sm transition-colors hover:bg-black/65 light:hover:bg-white disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
      onClick={(event) => {
        event.stopPropagation();
        onShare(filePath, fileType);
      }}
    >
      {sharing ? <Loader2 className="size-3.5 animate-spin" /> : <Share2 className="size-3.5" />}
    </button>
  );
};

const InlinePreviewFrame = ({
  children,
  filePath,
  fileType,
  onPreview,
  onQuote,
  onShare,
  sharingFilePath,
}: {
  children: ReactNode;
  filePath: string;
  fileType?: 'image' | 'video';
  onPreview?: (filePath: string) => void;
  onQuote?: (filePath: string) => void | Promise<void>;
  onShare?: (filePath: string, fileType: 'image' | 'video') => void;
  sharingFilePath?: string;
}) => (
  <div className="group relative w-full overflow-hidden rounded-12 border border-system-white08 bg-system-white04 text-left transition-colors hover:border-system-white16">
    <button type="button" className="block w-full text-left" onClick={() => onPreview?.(filePath)}>
      {children}
    </button>
    {fileType ? (
      <ShareButton
        filePath={filePath}
        fileType={fileType}
        sharing={sharingFilePath === filePath}
        className="top-2 right-2"
        onShare={onShare}
      />
    ) : null}
    <QuoteButton
      filePath={filePath}
      className={fileType && onShare ? 'top-2 right-11' : 'top-2 right-2'}
      onQuote={onQuote}
    />
  </div>
);

const InlineImagePreview = ({
  previewUrl,
  fileName,
  showWatermark,
}: {
  previewUrl: string;
  fileName: string;
  showWatermark: boolean;
}) => {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  return (
    <div className="flex h-44 w-full items-center justify-center bg-black/40">
      {size ? (
        <MediaWithWatermark
          width={size.width}
          height={size.height}
          className="relative h-full w-auto"
          showWatermark={showWatermark}
        >
          <img alt={fileName} src={previewUrl} className="size-full object-contain" loading="lazy" />
        </MediaWithWatermark>
      ) : (
        <img
          alt={fileName}
          src={previewUrl}
          className="max-h-full max-w-full object-contain"
          loading="lazy"
          onLoad={(event) => {
            const target = event.currentTarget;
            if (target.naturalWidth && target.naturalHeight) {
              setSize({ width: target.naturalWidth, height: target.naturalHeight });
            }
          }}
        />
      )}
    </div>
  );
};

const InlineVideoPreview = ({
  filePath,
  previewUrl,
  onPreview,
  onQuote,
  onShare,
  sharingFilePath,
}: {
  filePath: string;
  previewUrl: string;
  onPreview?: (filePath: string) => void;
  onQuote?: (filePath: string) => void | Promise<void>;
  onShare?: (filePath: string, fileType: 'image' | 'video') => void;
  sharingFilePath?: string;
}) => {
  const t = useTranslations();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [showPauseHint, setShowPauseHint] = useState(false);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  const handleTogglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      void video.play().catch(() => undefined);
      setShowPauseHint(true);
      return;
    }

    video.pause();
    setShowPauseHint(false);
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (video?.videoWidth && video.videoHeight) {
      setSize({ width: video.videoWidth, height: video.videoHeight });
    }
  };

  const handlePreview = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    videoRef.current?.pause();
    onPreview?.(filePath);
  };

  return (
    <div className="group block w-full overflow-hidden rounded-12 border border-system-white08 bg-system-white04 text-left transition-colors hover:border-system-white16">
      <div className="relative h-44 overflow-hidden bg-black/60">
        <button
          type="button"
          className="block size-full cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-system-white36"
          onClick={handleTogglePlay}
        >
          <div className="flex size-full items-center justify-center">
            {size ? (
              <MediaWithWatermark
                width={size.width}
                height={size.height}
                className="relative h-full w-auto"
                showWatermark={SITE === 'china'}
              >
                <video
                  ref={videoRef}
                  className="size-full object-contain"
                  preload="metadata"
                  playsInline
                  src={previewUrl}
                  onLoadedMetadata={handleLoadedMetadata}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onEnded={() => setPlaying(false)}
                >
                  <track kind="captions" />
                </video>
              </MediaWithWatermark>
            ) : (
              <video
                ref={videoRef}
                className="max-h-full max-w-full object-contain"
                preload="metadata"
                playsInline
                src={previewUrl}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
              >
                <track kind="captions" />
              </video>
            )}
          </div>
          <div
            className={cn(
              'pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity',
              playing ? 'opacity-0' : 'opacity-100',
            )}
          >
            <div className="inline-flex size-9 items-center justify-center rounded-full bg-black/45 text-system-white backdrop-blur-sm">
              <Play className="ml-0.5 size-4 fill-current" />
            </div>
          </div>
          <div
            className={cn(
              'pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-500',
              showPauseHint ? 'opacity-100' : 'opacity-0',
            )}
            onTransitionEnd={() => setShowPauseHint(false)}
          >
            <div className="inline-flex size-9 items-center justify-center rounded-full bg-black/45 text-system-white backdrop-blur-sm">
              <Pause className="size-4 fill-current" />
            </div>
          </div>
        </button>
        <button
          type="button"
          aria-label={t('claw.file.preview.open')}
          className="absolute top-2 right-2 inline-flex size-7 items-center justify-center rounded-full bg-black/45 light:bg-white/80 light:text-[#111111] text-system-white backdrop-blur-sm transition-colors hover:bg-black/65 light:hover:bg-white"
          onClick={handlePreview}
        >
          <ExternalLink className="size-3.5" />
        </button>
        <ShareButton
          filePath={filePath}
          fileType="video"
          sharing={sharingFilePath === filePath}
          className="top-2 right-11"
          onShare={onShare}
        />
        <QuoteButton filePath={filePath} className={onShare ? 'top-2 right-20' : 'top-2 right-11'} onQuote={onQuote} />
      </div>
      <div className="truncate border-system-white08 border-t px-3 py-2 text-system-white64 text-xs">{filePath}</div>
    </div>
  );
};

const InlineAttachmentPreview = ({
  filePath,
  fileType,
  status,
  clawId,
  onPreview,
  onQuote,
  onShare,
  sharingFilePath,
}: InlineAttachmentPreviewProps) => {
  const t = useTranslations();
  const pathname = usePathname();
  const isShareSessionPage = pathname.includes('/claw/share-session');
  const normalizedPath = normalizePath(filePath);
  const previewType = getPreviewTypeFromPath(normalizedPath);
  const shouldFetchPreview = previewType === 'image' || previewType === 'video' || isTextPreviewFile(normalizedPath);
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [textPreview, setTextPreview] = useState<{ content: string; hasMore: boolean }>();
  const [loading, setLoading] = useState(false);
  const [errorKind, setErrorKind] = useState<'notFound' | 'failed'>();

  const fileName = useMemo(() => getFallbackFileName(normalizedPath), [normalizedPath]);
  const previewCacheKey = useMemo(() => {
    if (!normalizedPath) return '';
    return `${isShareSessionPage ? 'share' : clawId}:${normalizedPath}`;
  }, [clawId, isShareSessionPage, normalizedPath]);

  const handleQuote = useCallback(
    async (targetPath: string) => {
      if (!onQuote) return;
      if (!targetPath || (!clawId && !isShareSessionPage)) return;

      reportClawCreationOperation('addChat', targetPath);

      const query = isShareSessionPage
        ? { file_path: targetPath, claw_id: '1' }
        : { claw_id: clawId as string, file_path: targetPath };
      try {
        const { data, error } = await getDownloadUrl({ query });
        if (error || !data?.download_url) {
          const isNotFound = error?.reason === 'NotFound' || error?.code === 404;
          toast({
            type: 'error',
            message: t(isNotFound ? 'claw.file.fileDeleted' : 'claw.file.downloadFailed'),
          });
          return;
        }
        onQuote(targetPath);
      } catch {
        toast({ type: 'error', message: t('claw.file.downloadFailed') });
      }
    },
    [clawId, isShareSessionPage, onQuote],
  );

  useEffect(() => {
    if (!normalizedPath || !shouldFetchPreview || (!clawId && !isShareSessionPage)) {
      setPreviewUrl(undefined);
      setTextPreview(undefined);
      setLoading(false);
      setErrorKind(undefined);
      return;
    }

    const cachedPreview = attachmentPreviewCache.get(previewCacheKey);
    if (cachedPreview) {
      setPreviewUrl(cachedPreview.previewUrl);
      setTextPreview(cachedPreview.textPreview);
      setLoading(false);
      setErrorKind(undefined);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setPreviewUrl(undefined);
    setTextPreview(undefined);
    setErrorKind(undefined);

    const query = isShareSessionPage
      ? { file_path: normalizedPath, claw_id: '1' }
      : { claw_id: clawId as string, file_path: normalizedPath };

    getDownloadUrl({
      query,
    })
      .then(async ({ data, error }) => {
        if (cancelled) return;

        if (error || !data?.download_url) {
          setLoading(false);
          setErrorKind(error?.reason === 'NotFound' || error?.code === 404 ? 'notFound' : 'failed');
          return;
        }

        if (!isTextPreviewFile(normalizedPath)) {
          attachmentPreviewCache.set(previewCacheKey, { previewUrl: data.download_url });
          setPreviewUrl(data.download_url);
          setLoading(false);
          return;
        }

        const content = await fetch(data.download_url)
          .then((response) => (response.ok ? response.text() : null))
          .catch(() => null);

        if (cancelled) return;
        setLoading(false);
        if (content === null) {
          setErrorKind('failed');
          return;
        }
        const nextTextPreview = trimTextPreview(content);
        attachmentPreviewCache.set(previewCacheKey, { textPreview: nextTextPreview });
        setTextPreview(nextTextPreview);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
        setErrorKind('failed');
      });

    return () => {
      cancelled = true;
    };
  }, [clawId, isShareSessionPage, normalizedPath, previewCacheKey, shouldFetchPreview]);

  if (status === 'deleted') {
    if (previewType === 'image' || previewType === 'video') {
      return (
        <div className="w-full overflow-hidden rounded-12 border border-system-white08 bg-system-white04">
          <div className="flex h-44 flex-col items-center justify-center gap-2 text-system-white40">
            <FileX className="size-6" />
            <div className="px-3 text-center text-xs">{t('claw.file.fileDeleted')}</div>
          </div>
          <div className="truncate border-system-white08 border-t px-3 py-2 text-system-white40 text-xs">
            {normalizedPath}
          </div>
        </div>
      );
    }
    return <FileItem file_path={normalizedPath} file_type={fileType} status={status} onPreview={onPreview} />;
  }

  if (!normalizedPath || (previewType !== 'image' && previewType !== 'video' && !isTextPreviewFile(normalizedPath))) {
    return <FileItem file_path={normalizedPath} file_type={fileType} status={status} onPreview={onPreview} />;
  }

  const isVisualPreview = previewType === 'image' || previewType === 'video';
  const contentHeightClass = isVisualPreview ? 'h-44' : 'h-40';

  if (loading) {
    return (
      <InlinePreviewFrame
        filePath={normalizedPath}
        onPreview={onPreview}
        onShare={onShare}
        sharingFilePath={sharingFilePath}
      >
        <div className={cn('flex items-center justify-center text-system-white40', contentHeightClass)}>
          <Loader2 className="size-4 animate-spin" />
        </div>
        <div className="truncate border-system-white08 border-t px-3 py-2 text-system-white40 text-xs">
          {normalizedPath}
        </div>
      </InlinePreviewFrame>
    );
  }

  if (errorKind) {
    const Icon = errorKind === 'notFound' ? FileX : AlertTriangle;
    const message = t(errorKind === 'notFound' ? 'claw.file.fileDeleted' : 'claw.file.preview.failed');
    return (
      <div className="w-full overflow-hidden rounded-12 border border-system-white08 bg-system-white04">
        <div className={cn('flex flex-col items-center justify-center gap-2 text-system-white40', contentHeightClass)}>
          <Icon className="size-6" />
          <div className="px-3 text-center text-xs">{message}</div>
        </div>
        <div className="truncate border-system-white08 border-t px-3 py-2 text-system-white40 text-xs">
          {normalizedPath}
        </div>
      </div>
    );
  }

  if (previewType === 'image' && previewUrl) {
    return (
      <InlinePreviewFrame
        filePath={normalizedPath}
        fileType="image"
        onPreview={onPreview}
        onQuote={handleQuote}
        onShare={onShare}
        sharingFilePath={sharingFilePath}
      >
        <InlineImagePreview previewUrl={previewUrl} fileName={fileName} showWatermark={SITE === 'china'} />
        <div className="truncate border-system-white08 border-t px-3 py-2 text-system-white64 text-xs">
          {normalizedPath}
        </div>
      </InlinePreviewFrame>
    );
  }

  if (previewType === 'video' && previewUrl) {
    return (
      <InlineVideoPreview
        filePath={normalizedPath}
        previewUrl={previewUrl}
        onPreview={onPreview}
        onQuote={handleQuote}
        onShare={onShare}
        sharingFilePath={sharingFilePath}
      />
    );
  }

  if (textPreview) {
    return (
      <InlinePreviewFrame
        filePath={normalizedPath}
        onPreview={onPreview}
        onShare={onShare}
        sharingFilePath={sharingFilePath}
      >
        <div className={cn('relative overflow-hidden px-3 py-2', contentHeightClass)}>
          <ClawMarkdown
            content={textPreview.content}
            className="text-system-white64 text-xs leading-5"
            renderAsCodeBlock={!isMarkdownFile(normalizedPath)}
          />
        </div>
        <div className="truncate border-system-white08 border-t px-3 py-2 text-system-white64 text-xs">
          {normalizedPath}
        </div>
      </InlinePreviewFrame>
    );
  }

  return <FileItem file_path={normalizedPath} file_type={fileType} status={status} onPreview={onPreview} />;
};

export const MessageAttachmentList = ({
  files,
  clawId,
  align = 'left',
  shareSessionId,
  shareMessageId,
  onPreviewFile,
  onQuoteFile,
}: MessageAttachmentListProps) => {
  const { handleShareAsset, sharingPath } = useShareClawAsset({ activeClawId: clawId });
  const handleShareFile = useCallback(
    (filePath: string, fileType: 'image' | 'video') => {
      if (!shareSessionId || !shareMessageId) return;
      reportClawCreationOperation('share', filePath);
      handleShareAsset({ path: filePath }, fileType, {
        sessionId: shareSessionId,
        messageId: shareMessageId,
      });
    },
    [handleShareAsset, shareMessageId, shareSessionId],
  );

  if (!files.length) return null;

  const visualFileCount = files.filter((file) => {
    const previewType = getPreviewTypeFromPath(normalizePath(file.content ?? ''));
    return previewType === 'image' || previewType === 'video';
  }).length;
  const twoColumnGrid = visualFileCount > 1;

  return (
    <div
      className={cn(
        'grid w-full gap-2',
        twoColumnGrid ? 'max-w-[860px] grid-cols-2' : 'max-w-[520px] grid-cols-1',
        align === 'right' && 'justify-self-end',
      )}
    >
      {files.map((file, index) => {
        const normalizedPath = normalizePath(file.content ?? '');
        const previewType = getPreviewTypeFromPath(normalizedPath);
        const isVisualFile = previewType === 'image' || previewType === 'video';

        return (
          <div
            key={`${file.type ?? 'file'}-${file.content ?? ''}-${index}`}
            className={cn('w-full', twoColumnGrid && !isVisualFile && 'col-span-2')}
          >
            <InlineAttachmentPreview
              filePath={file.content ?? ''}
              fileType={file.type}
              status={file.status}
              clawId={clawId}
              onPreview={onPreviewFile}
              onQuote={onQuoteFile}
              onShare={shareSessionId && shareMessageId ? handleShareFile : undefined}
              sharingFilePath={sharingPath}
            />
          </div>
        );
      })}
    </div>
  );
};
